"use client";

import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { useEffect, useId, useRef, useState } from "react";

export function ChannelAbout({
  blurb,
  more,
}: {
  readonly blurb: string;
  readonly more: string | null;
}) {
  const text = more === null ? blurb : `${blurb} ${more}`;
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);
  const lineRef = useRef<HTMLSpanElement>(null);
  const descriptionId = useId();

  useEffect(() => {
    if (open) return;

    const line = lineRef.current;

    if (line === null) return;

    const observer = new ResizeObserver(() =>
      setClipped(line.scrollHeight > line.clientHeight),
    );
    observer.observe(line);
    return () => observer.disconnect();
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- Text can change the clipped content while its two-line box keeps the same size.
  }, [text, open]);

  return (
    <div {...stylex.props(styles.root, open && styles.expanded)}>
      <p id={descriptionId} {...stylex.props(styles.description)}>
        <span
          ref={lineRef}
          {...stylex.props(styles.text, !open && styles.clamped)}
        >
          {text}
        </span>
      </p>
      {open || clipped || text.length > 180 ? (
        <button
          type="button"
          aria-controls={descriptionId}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          {...stylex.props(styles.trigger, open && styles.expandedTrigger)}
        >
          {open ? "show less" : "read more"}
        </button>
      ) : null}
    </div>
  );
}

const styles = stylex.create({
  root: {
    display: "flex",
    minWidth: 0,
    alignItems: "flex-end",
    gap: spacing.space6,
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
    lineHeight: "20px",
    color: colors.secondary,
  },
  expanded: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: spacing.space8,
  },
  description: { margin: 0, minWidth: 0, flex: 1 },
  text: { whiteSpace: "pre-line" },
  clamped: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  trigger: {
    flexShrink: 0,
    alignSelf: "flex-end",
    borderWidth: 0,
    borderRadius: 3,
    padding: 0,
    backgroundColor: "transparent",
    fontFamily: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
    cursor: "pointer",
    fontWeight: 500,
    color: colors.foreground,
    textDecoration: { default: "none", ":hover": "underline" },
    textDecorationColor: colors.borderStrong,
    textUnderlineOffset: 3,
    ":focus-visible": {
      outlineWidth: 2,
      outlineStyle: "solid",
      outlineColor: colors.foreground,
      outlineOffset: 2,
    },
  },
  expandedTrigger: { alignSelf: "flex-start" },
});
